// src/config/seoConfig.js

const SITE_NAME = 'Tracklio Hub';
const SITE_URL  = 'https://trackliohub.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

// ── Shared location keywords (har page pe append honge) ───────
const LOCATION_KEYWORDS =
  'software company Delhi, software company Patna, software company Bihar, ' +
  'IT company Delhi, IT company Patna, IT company Bihar, ' +
  'software development company Delhi, software development Patna, ' +
  'best software company Delhi, best IT company Bihar, ' +
  'software company India, custom software development India';

// ── Organization schema (public pages pe use hoga) ────────────
const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Tracklio Hub',
  url: SITE_URL,
  logo: `${SITE_URL}/logo192.png`,
  description:
    'Tracklio Hub is a leading custom software development company in Delhi, Patna and Bihar India offering ERP, banking software, inventory management, MDM, CRM and HRMS solutions.',
  areaServed: [
    'Delhi', 'New Delhi', 'Bihar', 'Patna', 'Mumbai',
    'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata',
    'Pune', 'Noida', 'Gurgaon', 'Lucknow', 'India',
  ],
  address: [
    {
      '@type': 'PostalAddress',
      addressLocality: 'Delhi',
      addressRegion: 'Delhi',
      addressCountry: 'IN',
    },
    {
      '@type': 'PostalAddress',
      addressLocality: 'Patna',
      addressRegion: 'Bihar',
      addressCountry: 'IN',
    },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    availableLanguage: ['English', 'Hindi'],
  },
  sameAs: [
    'https://www.linkedin.com/company/trackliohub',
    'https://twitter.com/trackliohub',
  ],
};

// ─────────────────────────────────────────────────────────────
export const SEO_CONFIG = {

  // ══ PUBLIC PAGES ═════════════════════════════════════════════

  '/': {
    title: 'Best Software Company in Delhi, Patna Bihar | ERP, CRM, MDM & Custom Software Development India',
    description:
      'Tracklio Hub — Best software development company in Delhi, Patna and Bihar India. We provide ERP software, banking software, inventory management, MDM, CRM, HRMS and custom web & mobile app development for businesses. Free demo available.',
    keywords:
      // Brand
      'Tracklio Hub, trackliohub, tracklihub, tracklio software, ' +
      // Location — Delhi
      'software company in Delhi, best software company Delhi, IT company Delhi, ' +
      'software development company Delhi, custom software Delhi, ERP software Delhi, ' +
      'web development company Delhi, mobile app development Delhi, ' +
      // Location — Patna Bihar
      'software company in Patna, software company in Bihar, best software company Patna, ' +
      'IT company Bihar, software development Patna, custom software Bihar, ' +
      'ERP software Patna, web development Patna, app development Bihar, ' +
      // Location — India
      'software company India, IT company India, best software company India, ' +
      // Services
      'ERP software India, banking software India, banking software development, ' +
      'inventory management system India, inventory management software, ' +
      'MDM solution India, mobile device management software, Android MDM, iOS MDM, ' +
      'CRM software India, HRMS software India, HR software India, ' +
      'payroll software India, project management software India, ' +
      'custom software development India, web app development India, ' +
      'mobile app development India, React developer India, ' +
      'Node.js developer Delhi, enterprise software India, SaaS company India, ' +
      'DPC software India, cooperative banking software India, ' +
      'school management software India, hospital management software India, ' +
      'business management software India',
    ogType: 'website',
    schema: ORG_SCHEMA,
  },

  '/coming-soon': {
    title: 'Coming Soon — New Features | Tracklio Hub',
    description:
      'Exciting new features are coming to Tracklio Hub. Stay tuned for updates on our ERP, CRM, MDM and business management platform.',
    keywords:
      'Tracklio Hub coming soon, new features, software update, ' + LOCATION_KEYWORDS,
    ogType: 'website',
  },

  // ══ DASHBOARD — noIndex: true (Google crawl nahi karega) ═════

  '/dashboard': {
    title: 'Dashboard | Tracklio Hub',
    description: 'Tracklio Hub business management dashboard.',
    keywords: '',
    noIndex: true,
  },

  // ── CRM ──────────────────────────────────────────────────────

  '/dashboard/crm/leads': {
    title: 'CRM Leads Management | Tracklio Hub',
    description:
      'Track and manage all your sales leads in one place. Convert prospects to customers with Tracklio Hub CRM software.',
    keywords:
      'CRM leads management, sales leads tracking, lead conversion software India, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/crm/contacts': {
    title: 'CRM Contacts | Tracklio Hub',
    description:
      'Manage all your business contacts and customer relationships with Tracklio Hub CRM.',
    keywords:
      'contact management software, CRM contacts, customer database software India, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/crm/accounts': {
    title: 'CRM Accounts | Tracklio Hub',
    description:
      'Manage business accounts and client profiles with Tracklio Hub CRM platform.',
    keywords:
      'CRM accounts management, client management software, business accounts India, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/crm/opportunities': {
    title: 'CRM Opportunities | Tracklio Hub',
    description:
      'Track sales opportunities and close more deals with Tracklio Hub CRM.',
    keywords:
      'CRM opportunities, sales opportunities tracking, opportunity management India, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/crm/deals': {
    title: 'CRM Deals & Pipeline | Tracklio Hub',
    description:
      'Track your sales deals and manage pipeline stages efficiently with Tracklio Hub.',
    keywords:
      'deals management, sales pipeline software, CRM deals India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/crm/campaigns': {
    title: 'CRM Marketing Campaigns | Tracklio Hub',
    description:
      'Create and manage marketing campaigns to reach your target audience.',
    keywords:
      'marketing campaigns software, CRM campaign management India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── ENQUIRY ──────────────────────────────────────────────────

  '/dashboard/enquiry/all': {
    title: 'All Enquiries | Tracklio Hub',
    description: 'View and manage all business enquiries from one central dashboard.',
    keywords: 'enquiry management software, business enquiry tracking India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/enquiry/pending': {
    title: 'Pending Enquiries | Tracklio Hub',
    description: 'Track and follow up on all pending customer enquiries.',
    keywords: 'pending enquiry management, customer enquiry tracker, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/enquiry/followup': {
    title: 'Follow-up Enquiries | Tracklio Hub',
    description: 'Manage follow-up tasks for customer enquiries efficiently.',
    keywords: 'follow-up management, enquiry follow-up software, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/enquiry/converted': {
    title: 'Converted Enquiries | Tracklio Hub',
    description: 'Track converted enquiries and measure sales conversion rates.',
    keywords: 'converted leads, enquiry conversion tracking, sales conversion software, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── HRMS ─────────────────────────────────────────────────────

  '/dashboard/hrms/employees': {
    title: 'Employee Management — HRMS | Tracklio Hub',
    description:
      'Manage employee records, profiles and HR data with Tracklio Hub HRMS software.',
    keywords:
      'employee management software, HRMS India, HR software Delhi, ' +
      'employee database software, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/hrms/attendance': {
    title: 'Attendance Management — HRMS | Tracklio Hub',
    description:
      'Track employee attendance, working hours and shifts in real time.',
    keywords:
      'attendance management system, employee attendance tracker India, ' +
      'biometric attendance software, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/hrms/leave': {
    title: 'Leave Management — HRMS | Tracklio Hub',
    description:
      'Manage employee leave requests, approvals and leave balances with ease.',
    keywords:
      'leave management software India, employee leave tracker, HR leave system, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/hrms/payroll': {
    title: 'Payroll Management — HRMS | Tracklio Hub',
    description:
      'Automate salary processing, payslips and payroll compliance for your business.',
    keywords:
      'payroll software India, salary management software, payroll automation Delhi, ' +
      'payroll processing Bihar, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/hrms/recruitment': {
    title: 'Recruitment Management — HRMS | Tracklio Hub',
    description:
      'Streamline your hiring process from job posting to onboarding with Tracklio Hub.',
    keywords:
      'recruitment software India, hiring management system, HR recruitment Delhi, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/hrms/performance': {
    title: 'Performance Management — HRMS | Tracklio Hub',
    description:
      'Track and evaluate employee performance with goal setting and appraisal tools.',
    keywords:
      'performance management software, employee appraisal system India, ' +
      'HR performance tracking, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/hrms/training': {
    title: 'Training Management — HRMS | Tracklio Hub',
    description:
      'Plan and manage employee training programs and skill development.',
    keywords:
      'training management software, employee training system India, ' +
      'HR training module, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── PROJECTS ─────────────────────────────────────────────────

  '/dashboard/projects/all': {
    title: 'Project Management Software | Tracklio Hub',
    description:
      'Manage all your projects, tasks, milestones and team members in one platform.',
    keywords:
      'project management software India, task tracking software, team collaboration tool Delhi, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/projects/tasks': {
    title: 'Task Management | Tracklio Hub',
    description:
      'Create, assign and track tasks across all projects with ease.',
    keywords:
      'task management software India, team task tracker, project task tool, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/projects/milestones': {
    title: 'Project Milestones | Tracklio Hub',
    description:
      'Set and track project milestones to ensure timely delivery.',
    keywords:
      'project milestone tracking, milestone management software India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/projects/gantt': {
    title: 'Gantt Chart — Project Timeline | Tracklio Hub',
    description:
      'Visual project timeline and Gantt chart for better planning and scheduling.',
    keywords:
      'gantt chart software India, project timeline tool, visual project planning Delhi, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/projects/timetracking': {
    title: 'Time Tracking — Projects | Tracklio Hub',
    description:
      'Track time spent on tasks and projects for better productivity and billing.',
    keywords:
      'time tracking software India, employee time tracker, project time management, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/projects/sprints': {
    title: 'Sprint Management — Agile | Tracklio Hub',
    description:
      'Manage agile sprints, backlogs and sprint reviews for your development team.',
    keywords:
      'sprint management software, agile project management India, scrum tool Delhi, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/projects/bugs': {
    title: 'Bug Tracking — Projects | Tracklio Hub',
    description:
      'Track, report and resolve bugs efficiently with Tracklio Hub bug management.',
    keywords:
      'bug tracking software India, issue tracker, defect management tool, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/projects/team': {
    title: 'Team Members — Projects | Tracklio Hub',
    description:
      'Manage your project team, assign roles and track member contributions.',
    keywords:
      'team management software, project team tracker India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── MDM ──────────────────────────────────────────────────────

  '/dashboard/mdm/devices': {
    title: 'All Devices — MDM | Tracklio Hub',
    description:
      'Manage all Android and iOS devices from a single central dashboard.',
    keywords:
      'mobile device management India, MDM software Delhi, device management platform Bihar, ' +
      'enterprise MDM solution, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/customer': {
    title: 'MDM Customers | Tracklio Hub',
    description: 'Manage all MDM customers and their enrolled devices.',
    keywords: 'MDM customer management, device customer portal India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/android': {
    title: 'Android Device Management — MDM | Tracklio Hub',
    description:
      'Enroll, manage and secure Android devices across your entire organization.',
    keywords:
      'Android MDM India, android device management software, enterprise android management Delhi, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/ios': {
    title: 'iOS Device Management — MDM | Tracklio Hub',
    description:
      'Manage and secure Apple iOS devices with remote control and security policies.',
    keywords:
      'iOS MDM India, iPhone management software, Apple device management Bihar, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/groups': {
    title: 'Device Groups — MDM | Tracklio Hub',
    description:
      'Organize devices into groups and apply policies at the group level.',
    keywords:
      'MDM device groups, device group management India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/policies': {
    title: 'Device Policies — MDM | Tracklio Hub',
    description:
      'Configure and enforce security policies across all enrolled devices.',
    keywords:
      'MDM policies India, device security policy, enterprise device policy Delhi, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/apps': {
    title: 'App Management — MDM | Tracklio Hub',
    description:
      'Deploy and manage applications across all enrolled devices remotely.',
    keywords:
      'MDM app management, enterprise app deployment India, mobile app management, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/tracking': {
    title: 'Device Tracking — MDM | Tracklio Hub',
    description:
      'Real-time GPS tracking and location history for all enrolled devices.',
    keywords:
      'device GPS tracking, MDM location tracking India, real-time device tracking, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/remote-actions': {
    title: 'Remote Actions — MDM | Tracklio Hub',
    description:
      'Perform remote lock, wipe, restart and other actions on enrolled devices.',
    keywords:
      'remote device management India, MDM remote actions, remote wipe software, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/alerts': {
    title: 'Device Alerts — MDM | Tracklio Hub',
    description:
      'Monitor device health and receive real-time alerts for policy violations.',
    keywords:
      'MDM alerts, device monitoring software India, device health alerts, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/inventory': {
    title: 'Device Inventory — MDM | Tracklio Hub',
    description:
      'Complete hardware and software inventory of all enrolled devices.',
    keywords:
      'device inventory management, MDM inventory India, IT asset management, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/wifi': {
    title: 'WiFi Profiles — MDM | Tracklio Hub',
    description:
      'Deploy and manage WiFi configurations across all enrolled devices.',
    keywords:
      'MDM wifi profiles, device wifi management India, enterprise wifi deployment, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/updates': {
    title: 'OS Updates — MDM | Tracklio Hub',
    description:
      'Schedule and manage OS updates across all Android and iOS devices.',
    keywords:
      'MDM OS updates, device update management India, enterprise update management, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/compliance': {
    title: 'Compliance Reports — MDM | Tracklio Hub',
    description:
      'Monitor device compliance status and generate detailed compliance reports.',
    keywords:
      'MDM compliance reports, device compliance software India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── MASTER DATA ───────────────────────────────────────────────

  '/dashboard/mdm/products': {
    title: 'Product Master | Tracklio Hub',
    description: 'Manage your complete product master data in one place.',
    keywords: 'product master data, product management software India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/customers': {
    title: 'Customer Master | Tracklio Hub',
    description: 'Manage customer master records and account details.',
    keywords: 'customer master data, customer management software India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/vendors': {
    title: 'Vendor Master | Tracklio Hub',
    description: 'Manage vendor profiles, contracts and payment terms.',
    keywords: 'vendor master data, vendor management software India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/locations': {
    title: 'Location Master | Tracklio Hub',
    description: 'Manage business locations, warehouses and branch offices.',
    keywords: 'location master data, branch management software India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/mdm/categories': {
    title: 'Category Master | Tracklio Hub',
    description: 'Manage product and service categories for your business.',
    keywords: 'category master data, product categories management India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── INVENTORY ─────────────────────────────────────────────────

  '/dashboard/inventory/stock': {
    title: 'Stock Management — Inventory | Tracklio Hub',
    description:
      'Track stock levels, manage warehouse inventory and avoid stockouts.',
    keywords:
      'stock management software India, inventory tracking software Delhi, ' +
      'warehouse management Bihar, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/inventory/purchase-orders': {
    title: 'Purchase Orders — Inventory | Tracklio Hub',
    description:
      'Create and manage purchase orders with complete vendor tracking.',
    keywords:
      'purchase order management India, procurement software Delhi, PO management Bihar, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/inventory/transfer': {
    title: 'Stock Transfer — Inventory | Tracklio Hub',
    description:
      'Transfer stock between locations and warehouses with complete tracking.',
    keywords:
      'stock transfer software India, inventory transfer management, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/inventory/adjustment': {
    title: 'Stock Adjustment — Inventory | Tracklio Hub',
    description:
      'Make stock adjustments and reconcile inventory discrepancies.',
    keywords:
      'stock adjustment software India, inventory adjustment system, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── WALLET ───────────────────────────────────────────────────

  '/dashboard/wallet/invoices': {
    title: 'Wallet Invoices | Tracklio Hub',
    description: 'View and manage all subscription invoices and billing history.',
    keywords: 'wallet invoices, billing management, subscription invoice India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── SALES ─────────────────────────────────────────────────────

  '/dashboard/sales/quotations': {
    title: 'Quotations — Sales | Tracklio Hub',
    description:
      'Create professional quotations and convert them to sales orders quickly.',
    keywords:
      'quotation software India, sales quotation tool Delhi, quote management Bihar, ' +
      LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/sales/orders': {
    title: 'Sales Orders | Tracklio Hub',
    description:
      'Manage sales orders from confirmation to delivery with real-time tracking.',
    keywords:
      'sales order management India, order processing software Delhi, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/sales/invoices': {
    title: 'Sales Invoices | Tracklio Hub',
    description:
      'Generate GST-compliant invoices and track payment status easily.',
    keywords:
      'invoice software India, GST invoice generator Delhi, billing software Bihar, ' +
      'sales invoice management, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/sales/payments': {
    title: 'Payments — Sales | Tracklio Hub',
    description:
      'Track payments received, pending and overdue for all sales transactions.',
    keywords:
      'payment tracking software India, sales payment management Delhi, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── REPORTS ───────────────────────────────────────────────────

  '/dashboard/reports/sales': {
    title: 'Sales Reports | Tracklio Hub',
    description: 'Generate detailed sales reports to track revenue and performance.',
    keywords: 'sales reports software India, sales analytics Delhi, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/reports/hr': {
    title: 'HR Reports | Tracklio Hub',
    description: 'Generate HR reports on attendance, payroll, leave and performance.',
    keywords: 'HR reports software India, HRMS reports Delhi, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/reports/projects': {
    title: 'Project Reports | Tracklio Hub',
    description: 'Track project progress, team performance and delivery timelines.',
    keywords: 'project reports software India, project analytics, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/reports/devices': {
    title: 'Device Reports — MDM | Tracklio Hub',
    description: 'Detailed reports on device inventory, compliance and usage.',
    keywords: 'MDM reports India, device reports software, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/reports/financial': {
    title: 'Financial Reports | Tracklio Hub',
    description: 'Generate financial reports for P&L, balance sheet and cash flow.',
    keywords: 'financial reports software India, accounting reports Delhi, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/reports/custom': {
    title: 'Custom Reports | Tracklio Hub',
    description: 'Build custom reports with your own filters, columns and data fields.',
    keywords: 'custom reports software India, report builder tool, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── COMMUNICATION ─────────────────────────────────────────────

  '/dashboard/communication/messages': {
    title: 'Messages | Tracklio Hub',
    description: 'Internal messaging and team communication within Tracklio Hub.',
    keywords: 'team messaging software, internal communication tool India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/communication/announcements': {
    title: 'Announcements | Tracklio Hub',
    description: 'Create and broadcast company-wide announcements to your team.',
    keywords: 'company announcements software, team broadcast tool India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/communication/notifications': {
    title: 'Notifications | Tracklio Hub',
    description: 'Manage all system notifications and alerts in one place.',
    keywords: 'notification management, alerts dashboard India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── WHITE LABELING ────────────────────────────────────────────

  '/dashboard/whitelabel/branding': {
    title: 'Brand Settings — White Label | Tracklio Hub',
    description: 'Customize branding for your white-label platform.',
    keywords: 'white label software India, custom branding software, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/whitelabel/seo': {
    title: 'SEO Settings — White Label | Tracklio Hub',
    description: 'Configure SEO settings for your white-label platform.',
    keywords: 'white label SEO settings, custom SEO software India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/whitelabel/domain': {
    title: 'Custom Domain — White Label | Tracklio Hub',
    description: 'Set up and manage custom domains for your white-label platform.',
    keywords: 'custom domain software India, white label domain setup, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── SETTINGS ──────────────────────────────────────────────────

  '/dashboard/settings/company': {
    title: 'Company Settings | Tracklio Hub',
    description: 'Configure your company profile, logo and business information.',
    keywords: 'company settings software, business profile management India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/settings/users': {
    title: 'User Management | Tracklio Hub',
    description: 'Manage all users, their profiles and access in your organization.',
    keywords: 'user management software India, employee accounts management, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/settings/roles': {
    title: 'Roles & Permissions | Tracklio Hub',
    description: 'Manage user roles and access permissions for your entire team.',
    keywords: 'roles permissions software India, access control management, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/settings/system': {
    title: 'System Settings | Tracklio Hub',
    description: 'Configure system-level settings and platform preferences.',
    keywords: 'system settings, platform configuration software, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
  '/dashboard/settings/integrations': {
    title: 'Integrations | Tracklio Hub',
    description: 'Connect Tracklio Hub with third-party tools and services.',
    keywords: 'software integrations India, API integrations, third-party integrations, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  // ── HELP ──────────────────────────────────────────────────────

  '/dashboard/help': {
    title: 'Help & Support | Tracklio Hub',
    description: 'Get help, documentation and support for Tracklio Hub platform.',
    keywords: 'Tracklio Hub help, software support India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },

  '/dashboard/subscriptions': {
    title: 'Subscription Management | Tracklio Hub',
    description: 'Manage your Tracklio Hub subscription plan and billing.',
    keywords: 'subscription management, billing plans India, ' + LOCATION_KEYWORDS,
    noIndex: true,
  },
};

// ─────────────────────────────────────────────────────────────
export const SITE_DEFAULTS = {
  siteName:      SITE_NAME,
  siteUrl:       SITE_URL,
  defaultImage:  DEFAULT_IMAGE,
  twitterHandle: '@trackliohub',
  locale:        'en_IN',
};